<template>
  <DefaultLayout>
    <div id="product-plp-layout" class="font-sans bg-white text-gray-900" v-if="!initialized || isCheckproductData || loading">
      <div class="px-[1.4286rem] py-[1rem] lg:p-[2.1429rem]">
        <div
          class="m-auto 
            max-w-[var(--max-w-container)] 
            xl:max-w-[var(--max-w-container-xl)] 
            2xl:max-w-[var(--max-w-container-2xl)] 
            flex items-end justify-between"
        >
          <HeaderSection
            class="capitalize"
            :title="headerTitle"
            :breadcrumbItems="breadcrumbItems"
          />
          <NuxtLink
            :aria-label="PRODUCT_CONTENT.QUICK_ORDER"
            :title="PRODUCT_CONTENT.QUICK_ORDER"
            :to="URL_CONFIG.QUICK_ORDER"
            id="quick-order"
            class="cursor-pointer hidden lg:flex items-center gap-2 text-ash-500 hover:text-gold-100 text-[1rem] font-normal"
            external
          >
            <Icon icon="rabbit-fast" />
            {{PRODUCT_CONTENT.QUICK_ORDER}}
          </NuxtLink>
        </div>
      </div>
      <div v-if="!isPlpReady" class="flex justify-center items-center border-t border-gray-300 py-20 min-h-[80vh]" aria-busy="true">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" aria-hidden="true"></div>
      </div>
      <template v-else>
        <hr class="hidden lg:block border-t border-gray-300 w-full" aria-hidden="true">
        <div class="mx-[1.4286rem] lg:m-auto max-w-[var(--max-w-container)] xl:max-w-[var(--max-w-container-xl)] 2xl:max-w-[var(--max-w-container-2xl)]">
          <FilterProducts />
        </div>
      <MobileProductFilter
        v-if="!hideFilter"
        :facets="facets"
        :selected-filters="selectedFilters"
        :facets-ui-version="facetsUiVersion"
        :error="error"
        :set-facet-values="setFacetValuesForCurrentFacets"
        :clear-all-facets="clearAllFacets"
        :sort-params="sortParams"
        :set-sort-params="setSortParams"
      />
      <FilterNavigation
        :start="startIndex"
        :end="endIndex"
        :totalResults="totalDisplayed"
        :selected-filters="selectedFilters"
        :sort-params="sortParams"
        :clear-all-facets="clearAllFacets"
        :set-sort-params="setSortParams"
        class="lg:block"
      />

      <div v-if="showProductGrid">
        <div
          class="m-auto max-w-[var(--max-w-container)] xl:max-w-[var(--max-w-container-xl)] 2xl:max-w-[var(--max-w-container-2xl)] grid grid-cols-1 mt-6"
          :class="!hideFilter ? 'lg:grid-cols-4' : 'lg:grid-cols-3'"
        >
          <ProductFilters
            v-if="!hideFilter"
            :facets="facets"
            :selected-filters="selectedFilters"
            :facets-ui-version="facetsUiVersion"
            :error="error"
            :set-facet-values="setFacetValuesForCurrentFacets"
            :clear-all-facets="clearAllFacets"
            class="lg:col-span-1 hidden lg:block"
          />

          <div v-if="showContent" class="lg:col-span-3 block">
            <div
              v-if="showTaiaInsert"
              class="px-[1.7143rem] -mx-3 lg:mx-0 lg:mr-[-1.0714rem] mt-6 mb-4 lg:px-3 lg:mt-0"
            >
              <TaiaAgenticBanner
                :title="agenticFilterInitContent?.title"
                :short-description="agenticFilterInitContent?.short_description"
                :focus-areas="agenticFocusAreas"
                :initial-expanded="true"
                @select-focus-area="onAgenticFocusAreaSelect"
              />
            </div>
            <ProductList :product-data="productList" :hide-taia-card="showTaiaInsert" />
          </div>
          <div v-else class="lg:col-span-3 block flex justify-center py-20">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>

        <Pagination
          v-if="showContent"
          class="mt-12"
          :initialPage="currentPage"
          :totalPages="totalPages"
          :showPageNumbers="true"
          :totalCount="totalDisplayed"
          @update:page="onPageChange"
        />
      </div>
      </template>
    </div>
    <div v-if="initialized && !isCheckproductData && !loading">
      <div
         v-if="isMobileBreakpoint && error != PRODUCT_CONTENT.PRODUCT_NOT_FOUND"
        class="mt-[7.1428571429rem] mb-[14.2857142857rem] px-[2.8571428571rem] text-center text-[1rem]"
      >
        <p class="text-ash-200 text-[1rem]">
          Sorry, we couldn't find any items with that search.
        </p>
        <NuxtLink
          :to="`${URL_CONFIG.PRODUCT_BASE_URL}`"
          aria-label="Back to all products"
          title="Back to all products"
          id="back-to-all-products-link"
          class="inline-block mt-[1.4286rem] underline text-ash-500"
          external
        >
          Back to all products
        </NuxtLink>
      </div>
      <div v-else>
        <Error />
      </div>
    </div>
  </DefaultLayout>
</template>

<script setup lang="ts">
import {computed, provide, ref, watch} from 'vue'
import { storeToRefs } from 'pinia'
import {useRoute, useRouter} from 'vue-router'
import {breakpointsTailwind, useBreakpoints} from '@vueuse/core';
import DefaultLayout from '~/layouts/default.vue'
import HeaderSection from '~/components/ui/organism/headerSection.vue'
import Icon from '~/components/ui/atom/icon.vue'
import FilterNavigation from '~/components/ui/organism/product/filterNavigation.vue'
import FilterProducts from '~/components/ui/organism/product/filterProducts.vue'
import ProductFilters from '~/components/ui/organism/product/productFilters.vue'
import ProductList from '~/components/ui/organism/productList.vue'
import MobileProductFilter from '~/components/ui/organism/product/mobileFilterDrawer.vue'
import Pagination from '~/components/ui/organism/pagination.vue'
import Error from '~/components/ui/atom/error.vue'
import {URL_CONFIG} from '~~/shared/utils/url.const'
import {PRODUCT_CONTENT} from '~~/shared/utils/contentTypes'
import type {BreadcrumbItem, Category, ContentItemTitle, FacetDict, ProductPagedResponse} from '~~/shared/types/common'
import {CATEGORIES} from '~/constants/categories'
import {resolvePlpMetaDescription} from '~~/shared/utils/plpMetaDescription'
import {useScrollToTop} from '~/composables/useScrollToTop'
import {useProducts} from '~/composables/useProducts'
import siteService from '~/composables/siteService'
import {useLocaleStore} from "~/stores/localeStore";
import type {LocationQueryValue} from "#vue-router";
import TaiaAgenticBanner from '~/components/ui/organism/product/agentic/taiaAgenticBanner.vue'
import { PRODUCT_SORT_OPTIONS } from '~/components/utils/sortOptions'
import { useAgenticFilterInit } from '~/composables/useAgenticFilter'
import { useIframeStore } from "~/stores/chatAIStore";

const route = useRoute()
const router = useRouter()
const breakPoints = useBreakpoints(breakpointsTailwind);
const isMobileBreakpoint = breakPoints.smaller('md');
const iframeStore = useIframeStore()
const { dynamicYieldData } = storeToRefs(iframeStore)

const props = withDefaults(
  defineProps<{
    heading?: string
    breadcrumbItems?: BreadcrumbItem[]
  }>(),
  {
    heading: 'Supplements',
    categoryLinks: () => [],
    items: (): ContentItemTitle[] => [],
    breadcrumbItems: () => [
      { label: 'Home', href: '/' },
      { label: 'Products', href: URL_CONFIG.PRODUCT_BASE_URL }
    ]
  }
)
const getQueryParamString = (value: LocationQueryValue | LocationQueryValue[] | undefined): string | undefined => {
  if (Array.isArray(value)) {
    const first = value[0]
    return typeof first === 'string' ? first : undefined
  }
  return typeof value === 'string' ? value : undefined
}

const sortParams:ComputedRef<{ sort: string }> = computed( () => {
  const sortQuery = getQueryParamString(route.query.sort as LocationQueryValue | LocationQueryValue[] | undefined)
  return { sort: sortQuery ?? 'recommendationRank,ASC' }
})

const routePageNumber = computed(() => {
  return Number.parseInt(route.params.page as string ?? '0') ?? 0
})

const routeCategory = computed(() => {
  const slug = route.params.slug
  if (!slug) return null
  return Array.isArray(slug) ? slug.join('/') : slug
})

const BUNDLE_SET_TITLE = 'Thorne Product Stacks'
const setUrlKey = computed(() => {
  const prefix = '/products/set/'
  if (!route.path.startsWith(prefix)) return ''
  return decodeURIComponent(route.path.slice(prefix.length).replace(/\/$/, ''))
})
const isSetPage = computed(() => Boolean(setUrlKey.value))

const rawFiltersFromRoute = (): Record<string, string[]> => {
  const rawFacetsFromUrl: Record<string, string[]> = {}
  const filterKeysFromUrl = Object.keys(route.query)
      .filter(key => key.startsWith('f['))

  for (const queryKey of filterKeysFromUrl) {
    const queryValues = route.query[queryKey]
    const values = Array.isArray(queryValues) ? queryValues : [queryValues]
    for (const filterValue of values) {
      if (typeof filterValue !== 'string') continue
      const underScoreIndex = filterValue.indexOf('_')
      if (underScoreIndex === -1) continue
      const facetKey = filterValue.slice(0, underScoreIndex)
      const facetValue = filterValue.slice(underScoreIndex + 1)
      if (!facetValue) continue
      ;(rawFacetsFromUrl[facetKey] ??= []).push(facetValue)
    }
  }

  return rawFacetsFromUrl
}

function urlToTitleCase(toCap: string) {
  return toCap.replaceAll('-', ' ').replace(
    /\w\S*/g,
    text => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase()
  )
}

function extractValueFromUrlCategory(categoryUrl: string) {
  if (categoryUrl.length > 0) {
      return urlToTitleCase(categoryUrl.split('/').pop() ?? "") as string
  }
  return ""
}

const routeCategoryToFilter = computed(() => {
  if (isSetPage.value) return undefined

  switch (true) {
    case routeCategory.value?.startsWith('by-health-need/'): {
      const category = CATEGORIES.find(({urlKey}) => urlKey === routeCategory.value)
      return category
        ? ({"Health Needs": category.facetValue} as Record<string, string>)
        : undefined
    }
    case routeCategory.value?.includes('by-ingredient/'):
      return ({"Ingredients": extractValueFromUrlCategory(routeCategory.value ?? "")} as Record<string, string>)
    case routeCategory.value?.includes('bestsellers'):
      return ({"Feature": "Bestsellers"} as Record<string, string>)
    case routeCategory.value?.includes('exclusives'):
      return ({"Feature": "Exclusive"} as Record<string, string>)
  }
  return undefined
})

const selectedFilters = computed(() => {
  const rawFacetsFromUrl = rawFiltersFromRoute()
  const validatedFacetsFromURL: Record<string, string[]> = {}
  const routeFilter: Record<string, string>|undefined = routeCategoryToFilter.value
  if (routeFilter) {
    for (const [facetType, value] of Object.entries(routeFilter)) {
      (rawFacetsFromUrl[facetType] ??= []).push(value)
    }
  }

  for (const [facetType, values] of Object.entries(rawFacetsFromUrl)) {
    // Before the first response, preserve deep-link filters for the request.
    if (!Object.keys(facets.value).length || !facets.value[facetType]) {
      validatedFacetsFromURL[facetType] = values
      continue
    }

    const validatedValues = validateFacetValuesFromUrl(facetType, values, facets.value)
    if (validatedValues.length) validatedFacetsFromURL[facetType] = validatedValues
  }

  return validatedFacetsFromURL
})

const localeStore = useLocaleStore()
const storeLocaleCode = computed(() => localeStore.locale?.localeCode)
const routeLocale = computed(() => {
  const routeQuery = route?.query?.locale
  return Array.isArray(routeQuery) ? routeQuery[0] : routeQuery
})
const resolvedLocale = computed(() =>
    (routeLocale.value ?? storeLocaleCode.value ?? 'en-US').replace('-', '_')
)

const pageSize = ref(0)
const totalDisplayed = ref(0)

const showTaiaInsert = computed(() =>
  isPlpReady.value &&
  (dynamicYieldData.value?.AgenticPLP ?? false) &&
  (resolvedLocale.value === 'en_US') && 
  shouldInitAgenticFilter.value &&
  !loading.value &&
  totalDisplayed.value > 0 &&
  showProductGrid.value &&
  !!agenticFilterInitContent.value
)

const getPageFromRoute = () => {
  const pageParam = route.query.page
  if (!pageParam) return 1

  const raw = Array.isArray(pageParam) ? pageParam[0] : pageParam
  const parsed = Number.parseInt(String(raw), 10)

  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1
}

const setSortParams = async (sort: string = 'recommendationRank,ASC') => {
  const nextQuery = {
    ...route.query,
    sort: sort
  }

  // Always reset pagination to start from first page when sorting changes
  delete nextQuery['page']

  await router.replace({
    path: route.path,
    query: nextQuery
  })
}

const currentPage = computed(() => getPageFromRoute())

const productCallData = ref<ProductPagedResponse | null>(null)
const facets = computed<FacetDict>(() => productCallData.value?.facets ?? {})

const productsComposable = useProducts()
const {
    loading,
    error,
    facetsUiVersion,
    setFacetValues,
    clearAllFacets,
    initFromUrl,
    isCheckproductData,
    initialized
} = productsComposable

const setFacetValuesForCurrentFacets = (facetName: string, values: string[]) => {
  setFacetValues(facetName, facets.value, values)
}

const stringifyLogDetails = (details: Record<string, unknown>) => {
  const seen = new WeakSet<object>()

  return JSON.stringify(details, (_key, value) => {
    if (value instanceof globalThis.Error) {
      return {
        name: value.name,
        message: value.message,
        stack: value.stack,
        cause: value.cause,
      }
    }

    if (value && typeof value === 'object') {
      if (seen.has(value)) return '[Circular]'
      seen.add(value)

      if ('message' in value || 'statusCode' in value || 'statusMessage' in value || 'status' in value || 'stack' in value) {
        const errorLike = value as {
          name?: unknown
          message?: unknown
          statusCode?: unknown
          status?: unknown
          statusMessage?: unknown
          data?: unknown
          stack?: unknown
          cause?: unknown
        }

        return {
          name: errorLike.name,
          message: errorLike.message,
          statusCode: errorLike.statusCode,
          status: errorLike.status,
          statusMessage: errorLike.statusMessage,
          data: errorLike.data,
          stack: errorLike.stack,
          cause: errorLike.cause,
        }
      }
    }

    return value
  })
}

const logProductsLayout = (message: string, details: Record<string, unknown>) => {
  console.info(`[products layout] ${message} ${stringifyLogDetails(details)}`)
}

const logProductsLayoutError = (message: string, details: Record<string, unknown>) => {
  console.error(`[products layout] ${message} ${stringifyLogDetails(details)}`)
}

let requestGeneration = 0
const refreshProducts = async () => {
  const generation = ++requestGeneration
  loading.value = true
  const productFetchMethod = isSetPage.value ? 'fetchProductSets' : 'fetchActiveProducts'
  const requestContext = {
    method: productFetchMethod,
    generation,
    routePath: route.path,
    locale: resolvedLocale.value,
    page: currentPage.value,
    filters: selectedFilters.value,
    sort: sortParams.value.sort,
  }
  logProductsLayout('refreshProducts:start', requestContext)

  try {
    const service = siteService()
    const args = [
      resolvedLocale.value,
      currentPage.value,
      selectedFilters.value,
      sortParams.value.sort
    ] as const
    const response = isSetPage.value
      ? await service.fetchProductSets(setUrlKey.value, ...args)
      : await service.fetchActiveProducts(...args)
    if (generation !== requestGeneration) {
      logProductsLayout('refreshProducts:staleResponse', {
        ...requestContext,
        currentGeneration: requestGeneration,
      })
      return
    }

    productCallData.value = response.data.value ?? null
    const requestError = response.error.value as { message?: string } | null
    error.value = requestError?.message ?? null
    isCheckproductData.value = !requestError && Boolean(productCallData.value?.data.length)
    logProductsLayout('refreshProducts:complete', {
      ...requestContext,
      hasData: Boolean(productCallData.value),
      productCount: productCallData.value?.data.length ?? 0,
      totalResults: productCallData.value?.page?.totalElements,
      error: error.value,
      isCheckproductData: isCheckproductData.value,
    })
  } catch (requestError) {
    if (generation !== requestGeneration) {
      logProductsLayout('refreshProducts:staleError', {
        ...requestContext,
        currentGeneration: requestGeneration,
        error: requestError,
      })
      return
    }
    productCallData.value = null
    error.value = requestError instanceof globalThis.Error ? requestError.message : 'Unable to load products'
    isCheckproductData.value = false
    logProductsLayoutError('refreshProducts:error', {
      ...requestContext,
      error: requestError,
    })
  } finally {
    if (generation === requestGeneration) loading.value = false
  }
}

await refreshProducts()

const productList = computed(() => productCallData.value?.data ?? [])

provide('productsIsCheckproductData', isCheckproductData)
provide('productsInitialized', initialized)

const isPlpReady = ref(false)

const storeProductInfo = () => {
  if (process.client) {
    const structuredProducts = (productList?.value ?? []).map(product => ({
      id: product?.id ?? null,
      sku: product?.sku ?? '',
      productName: product?.name ?? '',
      substituteProductIds: product?.substituteProductIds ?? [],
      benefits: product?.benefits ?? [],
    }))
    localStorage.setItem('plpProducts', JSON.stringify(structuredProducts))
  }
}

await initFromUrl()
isPlpReady.value = true
storeProductInfo()

const showContent = ref(false)
watch(loading, (val) => {
  if (val) {
    showContent.value = false
  } else {
    showContent.value = true
  }
}, { immediate: true })

const hasProductListData = computed(() => {
  const data = (productList.value as any)
  return Array.isArray(data) && data.length > 0
})

const showProductGrid = computed(
  () => loading.value || Object.keys(facets.value).length > 0 || hasProductListData.value
)

watch(
  [() => route.fullPath, resolvedLocale],
  async () => {
    await refreshProducts()
  }
)

const onPageChange = (page: number) => {
  if (page === currentPage.value) return

  router.replace({
    query: { ...route.query, page: String(page) }
  })
}

watch(
  productCallData,
  val => {
    totalDisplayed.value = Number(val?.page.totalElements ?? 0)
    pageSize.value = Number(val?.page.size ?? 0)
    storeProductInfo()
  },
  { immediate: true }
)

const totalPages = computed(() => {
  const n = productCallData.value?.page.totalPages
  return Math.max(1, Number.isFinite(Number(n)) ? Number(n) : 1)
})

const startIndex = computed(() => {
  if (!totalDisplayed.value || !pageSize.value) return 0
  return (currentPage.value - 1) * pageSize.value + 1
})

const endIndex = computed(() => {
  if (!totalDisplayed.value || !pageSize.value) return 0
  return Math.min(currentPage.value * pageSize.value, totalDisplayed.value)
})

const selectedCategoryTitle = computed(() => {
  const resolvedTitle: String | Category | undefined = resolvePageTitle()

  if (typeof resolvedTitle === "undefined") {
    return PRODUCT_CONTENT.SUPPLEMENTS_TESTS
  }
  if (typeof resolvedTitle === "string") {
    return resolvedTitle
  }

  return (resolvedTitle as Category).heading ?? PRODUCT_CONTENT.SUPPLEMENTS_TESTS
})

function resolvePageTitle() {
  if (setUrlKey.value === 'bundles') return BUNDLE_SET_TITLE

  const slug = route.params.slug
  if (!slug) return PRODUCT_CONTENT.SUPPLEMENTS_TESTS

  const path = Array.isArray(slug) ? slug.join('/') : slug
  const last = Array.isArray(slug) ? slug.at(-1) : slug

  return CATEGORIES.find(cat => cat.urlKey === path) ||
      CATEGORIES.find(cat => cat.urlKey.endsWith(`/${last}`))
}

const headerTitle = computed(() =>
  route.path.startsWith('/tests') ? 'At-Home Tests & Health Panels' : selectedCategoryTitle.value
)


const siteCategory = computed(() => headerTitle.value ?? null)

const siteSortOption = computed(() => {
  const option = PRODUCT_SORT_OPTIONS.find(sortOption => sortOption.sortp === sortParams.value.sort)
  return option?.label ?? 'Sort by Recommended'
})

const isAgenticProductsPage = computed(() => {
  const path = route.path.replace(/\/+$/, '')
  return path === URL_CONFIG.AGENTIC_INIT || path.startsWith(`${URL_CONFIG.AGENTIC_INIT}/`)
})

const isBeyondBaseProducts = computed(() => {
  const path = route.path.replace(/\/+$/, '')
  return path !== URL_CONFIG.PRODUCT_BASE_URL
})

const hasFacetQuery = computed(() => {
  const keys = Object.keys(route.query)
  return keys.some(key => key.toLowerCase().startsWith('f['))
})

const hasSelectedFilters = computed(() => Object.keys(selectedFilters.value).length > 0)

const shouldInitAgenticFilter = computed(() =>
  isPlpReady.value &&
  (dynamicYieldData.value?.AgenticPLP ?? false) &&
  (resolvedLocale.value === 'en_US') &&
  !isAgenticProductsPage.value &&
  (isBeyondBaseProducts.value || hasFacetQuery.value || hasSelectedFilters.value)
)


const { data: agenticFilterInitResponse } = useAgenticFilterInit({
  selectedFilters,
  siteCategory,
  siteSortOption,
  shouldInitFilter: shouldInitAgenticFilter
})

const agenticFilterInitContent = computed(() => agenticFilterInitResponse.value?.content ?? null)
const agenticFocusAreas = computed(() =>
  agenticFilterInitResponse.value?.facet_filters
    ?.filter(filter => filter.filter_name && filter.display_name) ?? []
)

const AGENTIC_FILTER_STORAGE_KEY = 'agenticfilterobject'

const onAgenticFocusAreaSelect = async (filterName: string) => {
  if (!process.client) return

  const filterObject = {
    filter_id: agenticFilterInitResponse.value?.filter_id ?? '',
    filter_state: agenticFilterInitResponse.value?.filter_state ?? '',
    agenticfilters: [],
    filterkey: filterName
  }

  localStorage.removeItem(AGENTIC_FILTER_STORAGE_KEY)

  localStorage.setItem(
    AGENTIC_FILTER_STORAGE_KEY,
    JSON.stringify(filterObject)
  )

   await navigateTo(
    {
      path: URL_CONFIG.AGENTIC_INIT,
      query: {
        filterkey: filterName
      }
    },
    { external: true }
  )
  
}


useScrollToTop(currentPage)

const hideFilter = isSetPage

watch(
  () => [route.query.sort],
  () => {
    const sort = typeof route.query.sort === 'string' ? route.query.sort : null
    if (sort) {
      const current = sortParams?.value
      if (!current || current.sort !== sort) {
        setSortParams(sort)
      }
    }
  },
  { immediate: true }
)

const selectedCategoryMetaTitle = computed(() => {
  const resolvedTitle: String | Category | undefined = resolvePageTitle()

  if (typeof resolvedTitle === "undefined") {
    return PRODUCT_CONTENT.SUPPLEMENTS_TESTS
  }
  if (typeof resolvedTitle === "string") {
    return resolvedTitle
  }

  return (resolvedTitle as Category).metaTitle ?? PRODUCT_CONTENT.SUPPLEMENTS_TESTS

})

const headerMetaTitle = computed(() =>
  route.path.startsWith('/tests') ? 'At-Home Tests & Health Panels' : selectedCategoryMetaTitle.value
)

const headerMetaDescription = computed(() =>
  resolvePlpMetaDescription({
    path: route.path,
    slug: route.params.slug as string | string[] | undefined,
    isBundles: setUrlKey.value === 'bundles',
  })
)

useHead(() => ({
  title: `${headerMetaTitle.value} | Thorne`,
  meta: [
    {
      name: 'description',
      content: headerMetaDescription.value,
    },
  ],
}))

const cleanValueForFacet = (
    facetName: string,
    raw: string,
    all: FacetDict
): string | null => {
  if (!raw) return null
  let rawValue = raw
  const prefix = `${facetName}_`
  while (rawValue.startsWith(prefix)) rawValue = rawValue.slice(prefix.length)
  rawValue = rawValue.replaceAll('+', '')
  const allowed = new Set(all[facetName]?.values.map(value => value.value) ?? [])
  return allowed.has(rawValue) ? rawValue : null
}

function validateFacetValuesFromUrl (
    facetName: string,
    incoming: string[],
    all: FacetDict
): string[] {
  const values = new Set(all[facetName]?.values.map(value => value.value) ?? [])
  return incoming.filter(value => values.has(value))
}

</script>
